import { ModalController } from '@ionic/angular';
import type { ModalOptions } from '@ionic/core';

let isPatched = false;

/**
 * Globally patches ModalController.create(...) so that **every** modal in the app
 * (no matter where it's created from raw ModalController) supports swipe-to-close.
 *
 * We ensure `canDismiss: true` (modern replacement for the deprecated `swipeToClose`)
 * and `backdropDismiss: true`.
 *
 * This + the global Left-to-Right custom gesture (attached on ionModalDidPresent)
 * guarantees swipe-close behavior everywhere.
 *
 * PREFERRED (for new code):
 *   this.modalService.open(...) or this.modalService.openSwipeableSheet(...)
 *   (the service also calls into the patched create + attaches the custom gesture)
 */
export function forceSwipeToCloseOnAllModals(): void {
  if (isPatched) {
    return;
  }

  isPatched = true;

  const originalCreate = ModalController.prototype.create;

  ModalController.prototype.create = function (
    opts?: ModalOptions
  ): Promise<HTMLIonModalElement> {
    // Start from a copy so we can mutate safely. Use any locally because we delete props
    // and the exact ModalOptions generic from @ionic/core vs @ionic/angular can be picky.
    const forcedOptions: any = { ...(opts || {}) };

    // Remove the deprecated prop entirely so it is never passed to Ionic core.
    // (prevents deprecation warnings and ensures we control the behavior)
    delete forcedOptions.swipeToClose;

    // To ensure **ALL** modals created via any ModalController can be closed by swiping:
    // we **always** inject the modern `canDismiss: true`.
    // This (combined with our global custom LTR gesture) guarantees swipe-close.
    forcedOptions.canDismiss = true;
    forcedOptions.backdropDismiss = forcedOptions.backdropDismiss ?? true;

    return originalCreate.call(this, forcedOptions);
  };
}
